import { useRef } from 'react';
import { TextAreaField } from 'modules/templateEditor/common/components';
import { PushContentDefinition } from 'modules/graphql/types';
import { FormField } from 'ui/formField';

import { constructFieldErrors, getIsEditMode } from '../../common/utils';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';
import { getRestrictionText } from '../utils';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { constructErrorInlineText } from '../../common/ErrorInlineText';
import { FieldNames, MediumDataKey } from '../../common/types';
import { StyledTooltip } from '../styles';

export const PUSH_MESSAGE_MEDIUM_DATA_KEY: MediumDataKey = 'message';

export const BodyTextArea = () => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const pushData = (liveTemplate?.mediumData || {}) as PushContentDefinition;
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();

  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const mediumDataValue =
    !isEditMode && pushData.isSilentPush
      ? SETTINGS_DISPLAY_CONTENT.deliverSilently.silenced
      : pushData.message || '';

  const { hasError: hasPushMessageError, hasWarning: hasPushMessageWarning } = constructFieldErrors(
    {
      contentErrors: localeContentErrors,
      contentWarnings: localeContentWarnings,
      fieldName: FieldNames.MESSAGE,
    },
  );

  const bodyPushMessageErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: hasPushMessageError,
    hasWarning: hasPushMessageWarning,
  });

  return (
    <FormField
      isDisabled={pushData.isSilentPush}
      isHorizontalLayout
      hasError={hasPushMessageError}
      hasWarning={hasPushMessageWarning}
      warningText={!pushData.isSilentPush && bodyPushMessageErrorText}
      errorText={bodyPushMessageErrorText}
      labelText={SETTINGS_DISPLAY_CONTENT.body.labelText}
      restrictionText={getRestrictionText(pushData.message, isEditModeVisible)}
    >
      <StyledTooltip
        isEnabled={pushData.isSilentPush && isEditMode}
        tooltipContent={SETTINGS_DISPLAY_CONTENT.deliverSilently.restriction}
      >
        <TextAreaField
          isDisabled={pushData.isSilentPush}
          hasError={hasPushMessageError}
          mediumRef={bodyRef}
          mediumDataKey={PUSH_MESSAGE_MEDIUM_DATA_KEY}
          mediumDataValue={mediumDataValue}
          onChangeHandler={(msg) => setCurrentMediumField('message', msg)}
        />
      </StyledTooltip>
    </FormField>
  );
};
